import { z } from "zod";

/**
 * Integration categories as defined in the product specification and registry.
 */
export const integrationCategories = [
  "source_control",
  "model_provider",
  "tools_mcp",
  "chat_notifications",
  "issue_trackers",
  "package_registries",
  "webhooks",
  "identity",
  "billing",
] as const;
export const integrationCategorySchema = z.enum(integrationCategories);
export type IntegrationCategory = z.infer<typeof integrationCategorySchema>;

/**
 * Authentication methods supported by integrations.
 */
export const integrationAuthMethods = [
  "github_app",
  "oauth",
  "api_key",
  "personal_token",
  "mcp_connection",
  "webhook",
  "hmac_signature",
  "egress_allowlist",
  "oidc",
] as const;
export const integrationAuthMethodSchema = z.enum(integrationAuthMethods);
export type IntegrationAuthMethod = z.infer<typeof integrationAuthMethodSchema>;

/**
 * Risk classification for integration capabilities.
 */
export const integrationRisks = ["read", "write", "destructive"] as const;
export const integrationRiskSchema = z.enum(integrationRisks);
export type IntegrationRisk = z.infer<typeof integrationRiskSchema>;

/**
 * Closed action classes for write and destructive effects.
 */
export const actionClasses = [
  "git_push",
  "pull_request_create",
  "issue_update",
  "pr_comment",
  "deploy",
  "rollback",
] as const;
export const actionClassSchema = z.enum(actionClasses);
export type ActionClass = z.infer<typeof actionClassSchema>;

/**
 * Availability status of an integration type.
 */
export const integrationStatuses = ["available", "planned", "deprecated"] as const;
export const integrationStatusSchema = z.enum(integrationStatuses);
export type IntegrationStatus = z.infer<typeof integrationStatusSchema>;

/**
 * Canonical registered integration type schema.
 */
export const integrationTypeSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  category: integrationCategorySchema,
  description: z.string(),
  authMethods: z.array(integrationAuthMethodSchema).min(1),
  supportedScopes: z.array(z.string()),
  risk: integrationRiskSchema,
  actionClass: actionClassSchema.nullable().optional(),
  egressHosts: z.array(z.string()),
  status: integrationStatusSchema,
});
export type IntegrationType = z.infer<typeof integrationTypeSchema>;

export const integrationTypeListSchema = z.array(integrationTypeSchema);
export type IntegrationTypeList = z.infer<typeof integrationTypeListSchema>;

/**
 * Canonical list of built-in integration types.
 */
export const canonicalIntegrationTypes: readonly IntegrationType[] = [
  {
    id: "github",
    name: "GitHub",
    category: "source_control",
    description: "Repository access, pull request creation, and check inspection",
    authMethods: ["github_app", "oauth", "personal_token"],
    supportedScopes: ["repo:read", "repo:write", "pr:read", "pr:write", "status:read"],
    risk: "write",
    actionClass: "pull_request_create",
    egressHosts: ["github.com", "api.github.com"],
    status: "available",
  },
  {
    id: "anthropic",
    name: "Anthropic",
    category: "model_provider",
    description: "Model provider access through the platform broker or direct BYOK key",
    authMethods: ["api_key"],
    supportedScopes: ["model:invoke"],
    risk: "read",
    actionClass: null,
    egressHosts: ["api.anthropic.com", "zrouter.workgo.space"],
    status: "available",
  },
  {
    id: "openai",
    name: "OpenAI",
    category: "model_provider",
    description: "OpenAI model family access through the broker or direct BYOK key",
    authMethods: ["api_key"],
    supportedScopes: ["model:invoke"],
    risk: "read",
    actionClass: null,
    egressHosts: ["api.openai.com", "zrouter.workgo.space"],
    status: "available",
  },
  {
    id: "postgres_mcp",
    name: "Postgres MCP",
    category: "tools_mcp",
    description: "Database query inspection and schema tools via Model Context Protocol",
    authMethods: ["mcp_connection", "api_key"],
    supportedScopes: ["db:query_readonly", "db:schema_read"],
    risk: "read",
    actionClass: null,
    egressHosts: ["*"],
    status: "available",
  },
  {
    id: "custom_mcp",
    name: "Custom MCP Server",
    category: "tools_mcp",
    description:
      "Customer-hosted tool server speaking Model Context Protocol over secure transport",
    authMethods: ["mcp_connection", "oauth", "api_key"],
    supportedScopes: ["tools:invoke"],
    risk: "write",
    actionClass: "issue_update",
    egressHosts: ["*"],
    status: "available",
  },
  {
    id: "slack",
    name: "Slack",
    category: "chat_notifications",
    description: "Workspace notifications, channel updates, and approval alerts",
    authMethods: ["oauth", "webhook"],
    supportedScopes: ["chat:post", "chat:read"],
    risk: "write",
    actionClass: "pr_comment",
    egressHosts: ["slack.com", "api.slack.com"],
    status: "available",
  },
  {
    id: "ms_teams",
    name: "Microsoft Teams",
    category: "chat_notifications",
    description: "Enterprise channel notifications and approval interactive cards",
    authMethods: ["oauth", "webhook"],
    supportedScopes: ["chat:post"],
    risk: "write",
    actionClass: "pr_comment",
    egressHosts: ["graph.microsoft.com"],
    status: "planned",
  },
  {
    id: "jira",
    name: "Jira",
    category: "issue_trackers",
    description: "Issue link synchronization, work item creation, and ticket status progression",
    authMethods: ["oauth", "api_key"],
    supportedScopes: ["issue:read", "issue:write", "link:manage"],
    risk: "write",
    actionClass: "issue_update",
    egressHosts: ["atlassian.net", "api.atlassian.com"],
    status: "available",
  },
  {
    id: "linear",
    name: "Linear",
    category: "issue_trackers",
    description: "Project and ticket management synchronization for development teams",
    authMethods: ["oauth", "api_key"],
    supportedScopes: ["issue:read", "issue:write"],
    risk: "write",
    actionClass: "issue_update",
    egressHosts: ["api.linear.app"],
    status: "planned",
  },
  {
    id: "gitlab",
    name: "GitLab",
    category: "source_control",
    description: "GitLab repository access, merge request workflows, and pipeline triggers",
    authMethods: ["oauth", "personal_token"],
    supportedScopes: ["repo:read", "repo:write", "mr:write"],
    risk: "write",
    actionClass: "pull_request_create",
    egressHosts: ["gitlab.com"],
    status: "planned",
  },
  {
    id: "npm_registry",
    name: "npm Registry",
    category: "package_registries",
    description: "Package registry access for dependency verification and installation",
    authMethods: ["egress_allowlist"],
    supportedScopes: ["package:fetch"],
    risk: "read",
    actionClass: null,
    egressHosts: ["registry.npmjs.org"],
    status: "available",
  },
  {
    id: "go_proxy",
    name: "Go Proxy",
    category: "package_registries",
    description: "Go module proxy for dependency resolution and checksum verification",
    authMethods: ["egress_allowlist"],
    supportedScopes: ["module:fetch"],
    risk: "read",
    actionClass: null,
    egressHosts: ["proxy.golang.org"],
    status: "available",
  },
  {
    id: "crates_io",
    name: "crates.io",
    category: "package_registries",
    description: "Rust package registry access through verified egress allowlist",
    authMethods: ["egress_allowlist"],
    supportedScopes: ["crate:fetch"],
    risk: "read",
    actionClass: null,
    egressHosts: ["crates.io"],
    status: "available",
  },
  {
    id: "webhook_outbound",
    name: "Outbound Webhook",
    category: "webhooks",
    description: "Cryptographically signed event deliveries to external customer endpoints",
    authMethods: ["hmac_signature"],
    supportedScopes: ["event:deliver"],
    risk: "write",
    actionClass: "pr_comment",
    egressHosts: ["*"],
    status: "available",
  },
  {
    id: "google_sso",
    name: "Google SSO",
    category: "identity",
    description:
      "Managed OpenID Connect identity provider for domain verification and authentication",
    authMethods: ["oidc"],
    supportedScopes: ["identity:verify", "profile:read"],
    risk: "read",
    actionClass: null,
    egressHosts: ["accounts.google.com"],
    status: "available",
  },
  {
    id: "generic_payment",
    name: "Payment Provider",
    category: "billing",
    description: "Card checkout, invoices, and credit pack billing processing",
    authMethods: ["api_key", "webhook"],
    supportedScopes: ["billing:checkout", "billing:webhook"],
    risk: "write",
    actionClass: "issue_update",
    egressHosts: ["*"],
    status: "available",
  },
] as const;

const registeredTypeMap = new Map<string, IntegrationType>(
  canonicalIntegrationTypes.map((t) => [t.id, t]),
);

/**
 * Validate that a provider string identifies a registered integration type.
 * Throws or returns the type definition.
 */
export function validateIntegrationProvider(provider: string): IntegrationType {
  const trimmed = provider.trim();
  const match = registeredTypeMap.get(trimmed);
  if (!match) {
    throw new Error(`Unknown or unregistered integration provider: "${provider}"`);
  }
  return match;
}

/**
 * Check if a provider is registered.
 */
export function isRegisteredIntegrationProvider(provider: string): boolean {
  return registeredTypeMap.has(provider.trim());
}

/**
 * Validate that a connection uses a registered integration type with a supported auth method.
 */
export const integrationConnectionSchema = z
  .object({
    id: z.string().uuid(),
    projectId: z.string().uuid(),
    provider: z.string().refine((p) => isRegisteredIntegrationProvider(p), {
      message: "Provider must reference a registered integration type",
    }),
    authMethod: integrationAuthMethodSchema,
    credentialRef: z.string().min(1),
    scopes: z.array(z.string()).default([]),
  })
  .superRefine((data, ctx) => {
    const typeDef = registeredTypeMap.get(data.provider);
    if (!typeDef) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Unknown provider "${data.provider}"`,
        path: ["provider"],
      });
      return;
    }
    if (!typeDef.authMethods.includes(data.authMethod)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Auth method "${data.authMethod}" is not supported by ${typeDef.name}. Supported: ${typeDef.authMethods.join(", ")}`,
        path: ["authMethod"],
      });
    }
    for (const scope of data.scopes) {
      if (!typeDef.supportedScopes.includes(scope)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Scope "${scope}" is not supported by ${typeDef.name}. Supported: ${typeDef.supportedScopes.join(", ")}`,
          path: ["scopes"],
        });
      }
    }
  });

export type IntegrationConnection = z.infer<typeof integrationConnectionSchema>;
